/**
 * PoseNet Extension for Scratch (PictoBlox-compatible)
 * =====================================================
 * This extension uses the 'posenet' ID to match PictoBlox .sb3 project files.
 * It covers both Pose Detection (17 body keypoints) and Hand Detection
 * (21 hand keypoints) in one extension, matching PictoBlox's Human Body
 * Detection extension exactly.
 *
 * Uses TensorFlow.js MoveNet for pose estimation.
 */

import * as tf from '@tensorflow/tfjs';
import * as poseDetection from '@tensorflow-models/pose-detection';
import formatMessage from 'format-message';

// ── PoseNet body keypoint mapping (17 points) ──────────────────
// Keys are 0-indexed to match the 'parts' menu values used by PictoBlox
const BODY_PART_MAP = {
    0: 'nose',
    1: 'left_eye',
    2: 'right_eye',
    3: 'left_ear',
    4: 'right_ear',
    5: 'left_shoulder',
    6: 'right_shoulder',
    7: 'left_elbow',
    8: 'right_elbow',
    9: 'left_wrist',
    10: 'right_wrist',
    11: 'left_hip',
    12: 'right_hip',
    13: 'left_knee',
    14: 'right_knee',
    15: 'left_ankle',
    16: 'right_ankle'
};

// ── Hand / finger keypoint mapping (21 points) ─────────────────
// PictoBlox uses "fingerNumber" menu with these values
const FINGER_PART_MAP = {
    1: 0, // wrist
    2: 1, // thumb_cmc
    3: 2, // thumb_mcp
    4: 3, // thumb_ip
    5: 4, // thumb_tip
    6: 5, // index_finger_mcp
    7: 6, // index_finger_pip
    8: 7, // index_finger_dip
    9: 8, // index_finger_tip
    10: 9, // middle_finger_mcp
    11: 10, // middle_finger_pip
    12: 11, // middle_finger_dip
    13: 12, // middle_finger_tip
    14: 13, // ring_finger_mcp
    15: 14, // ring_finger_pip
    16: 15, // ring_finger_dip
    17: 16, // ring_finger_tip
    18: 17, // pinky_mcp
    19: 18, // pinky_pip
    20: 19, // pinky_dip
    21: 20 // pinky_tip
};

// Skeleton connections for drawing
const SKELETON_CONNECTIONS = [
    ['left_shoulder', 'right_shoulder'],
    ['left_shoulder', 'left_elbow'],
    ['left_elbow', 'left_wrist'],
    ['right_shoulder', 'right_elbow'],
    ['right_elbow', 'right_wrist'],
    ['left_shoulder', 'left_hip'],
    ['right_shoulder', 'right_hip'],
    ['left_hip', 'right_hip'],
    ['left_hip', 'left_knee'],
    ['left_knee', 'left_ankle'],
    ['right_hip', 'right_knee'],
    ['right_knee', 'right_ankle'],
    ['left_eye', 'nose'],
    ['right_eye', 'nose'],
    ['left_ear', 'left_eye'],
    ['right_ear', 'right_eye']
];

class PosenetExtension {
    constructor (runtime) {
        this.runtime = runtime;

        // ── Internal state ──────────────────────────────────────
        this._poseModelLoaded = false;
        this._poseModelLoading = false;

        this._poseDetector = null;

        this._poses = []; // array of pose results
        this._hands = []; // array of hand results (from pose wrists)
        this._drawBBox = false;

        // Video / overlay
        this._video = null;
        this._videoReady = false;
        this._overlayCanvas = null;
        this._overlayCtx = null;

        if (this.runtime) {
            this.runtime.on('PROJECT_STOP_ALL', () => {
                this._stopVideo();
            });
        }
        
        // Trigger model loading immediately upon extension initialization
        this._loadModelInit();
    }

    async _loadModelInit() {
        if (this.runtime) {
            this.runtime.emit('MODEL_LOADING', 'Posenet');
        }
        await this._loadPoseModel();
        if (this.runtime) {
            this.runtime.emit('MODEL_LOADED', 'Posenet');
        }
    }

    // ═══════════════════════════════════════════════════════════
    //  getInfo — block definitions
    //
    //  Extension ID = 'posenet' to match PictoBlox .sb3 format.
    //  Opcodes match PictoBlox exactly.
    // ═══════════════════════════════════════════════════════════

    _stopVideo () {
        if (this._video && this._video.srcObject) {
            const tracks = this._video.srcObject.getTracks();
            tracks.forEach(track => track.stop());
            this._video.srcObject = null;
        }
        this._videoReady = false;

        if (this._overlayCtx && this._overlayCanvas) {
            this._overlayCtx.clearRect(0, 0, this._overlayCanvas.width, this._overlayCanvas.height);
        }
    }

    getInfo () {
        return {
            id: 'posenet',
            name: formatMessage({
                id: 'gui.extension.posenet.name',
                default: 'Human Body Detection'
            }),
            color1: '#9966FF',
            color2: '#774DCB',
            blocks: [
                // ── General ─────────────────────────────────────
                {
                    opcode: 'toggleStageVideoFeed',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'posenet.blocks.turnVideo',
                        default: 'turn [VIDEO_STATE] video on stage with [TRANSPARENCY] % transparency'
                    }),
                    arguments: {
                        VIDEO_STATE: {
                            type: 'string',
                            menu: 'videoStateMenu',
                            defaultValue: 'on'
                        },
                        TRANSPARENCY: {
                            type: 'number',
                            defaultValue: 0
                        }
                    }
                },
                {
                    opcode: 'drawBoundingBox',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'posenet.blocks.showDetections',
                        default: '[OPTION] detections'
                    }),
                    arguments: {
                        OPTION: {
                            type: 'string',
                            menu: 'showHideMenu',
                            defaultValue: '1'
                        }
                    }
                },

                // ── Pose Detection ──────────────────────────────
                '---',
                {
                    opcode: 'analyseImage',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'posenet.blocks.analysePose',
                        default: 'analyse image for human pose from [FEED]'
                    }),
                    arguments: {
                        FEED: {
                            type: 'string',
                            menu: 'feedMenu',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'getPeopleCount',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'posenet.blocks.getNumPeople',
                        default: 'get # of people'
                    })
                },
                {
                    opcode: 'getX',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'posenet.blocks.getPartX',
                        default: 'X position of [PART] of person [PERSON]'
                    }),
                    arguments: {
                        PART: {
                            type: 'string',
                            menu: 'parts',
                            defaultValue: '1'
                        },
                        PERSON: {
                            type: 'string',
                            menu: 'personNumbers',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'getY',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'posenet.blocks.getPartY',
                        default: 'Y position of [PART] of person [PERSON]'
                    }),
                    arguments: {
                        PART: {
                            type: 'string',
                            menu: 'parts',
                            defaultValue: '1'
                        },
                        PERSON: {
                            type: 'string',
                            menu: 'personNumbers',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'isDetected',
                    blockType: 'Boolean',
                    text: formatMessage({
                        id: 'posenet.blocks.isPartDetected',
                        default: 'is [PART] of person [PERSON] detected?'
                    }),
                    arguments: {
                        PART: {
                            type: 'string',
                            menu: 'parts',
                            defaultValue: '1'
                        },
                        PERSON: {
                            type: 'string',
                            menu: 'personNumbers',
                            defaultValue: '1'
                        }
                    }
                },

                // ── Hand Detection ──────────────────────────────
                '---',
                {
                    opcode: 'analyseImageHand',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'posenet.blocks.analyseHand',
                        default: 'analyse image for hand from camera'
                    })
                },
                {
                    opcode: 'isDetectedHand',
                    blockType: 'Boolean',
                    text: formatMessage({
                        id: 'posenet.blocks.isHandDetected',
                        default: 'is hand detected'
                    })
                },
                {
                    opcode: 'getPositionHand',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'posenet.blocks.fingerPartPosition',
                        default: '[POSITION] position of [PART] of [FINGER]'
                    }),
                    arguments: {
                        POSITION: {
                            type: 'string',
                            menu: 'positionXYMenu',
                            defaultValue: '1'
                        },
                        PART: {
                            type: 'string',
                            menu: 'partsHand',
                            defaultValue: '4'
                        },
                        FINGER: {
                            type: 'string',
                            menu: 'finger',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'getPositionNumberHand',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'posenet.blocks.handPartPosition',
                        default: '[POSITION] position of [PART]'
                    }),
                    arguments: {
                        POSITION: {
                            type: 'string',
                            menu: 'positionXYMenu',
                            defaultValue: '1'
                        },
                        PART: {
                            type: 'string',
                            menu: 'fingerNumber',
                            defaultValue: '4'
                        }
                    }
                },
                {
                    opcode: 'getHand',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'posenet.blocks.handPosition',
                        default: '[POSITION] position of hand'
                    }),
                    arguments: {
                        POSITION: {
                            type: 'string',
                            menu: 'positionXYMenu',
                            defaultValue: '1'
                        }
                    }
                }
            ],
            menus: {
                feedMenu: {
                    items: [
                        {text: 'camera', value: '1'},
                        {text: 'stage', value: '2'}
                    ]
                },
                videoStateMenu: {
                    items: ['on', 'off']
                },
                showHideMenu: {
                    items: [
                        {text: 'show', value: '1'},
                        {text: 'hide', value: '2'}
                    ]
                },
                positionXYMenu: {
                    items: [
                        {text: 'x', value: '1'},
                        {text: 'y', value: '2'}
                    ]
                },
                parts: {
                    acceptReporters: true,
                    items: [
                        {text: 'nose', value: '0'},
                        {text: 'left eye', value: '1'},
                        {text: 'right eye', value: '2'},
                        {text: 'left ear', value: '3'},
                        {text: 'right ear', value: '4'},
                        {text: 'left shoulder', value: '5'},
                        {text: 'right shoulder', value: '6'},
                        {text: 'left elbow', value: '7'},
                        {text: 'right elbow', value: '8'},
                        {text: 'left wrist', value: '9'},
                        {text: 'right wrist', value: '10'},
                        {text: 'left hip', value: '11'},
                        {text: 'right hip', value: '12'},
                        {text: 'left knee', value: '13'},
                        {text: 'right knee', value: '14'},
                        {text: 'left ankle', value: '15'},
                        {text: 'right ankle', value: '16'}
                    ]
                },
                partsHand: {
                    acceptReporters: true,
                    items: [
                        {text: 'top', value: '4'},
                        {text: 'middle', value: '3'},
                        {text: 'bottom', value: '2'},
                        {text: 'base', value: '1'}
                    ]
                },
                finger: {
                    acceptReporters: true,
                    items: [
                        {text: 'thumb', value: '1'},
                        {text: 'index finger', value: '2'},
                        {text: 'middle finger', value: '3'},
                        {text: 'ring finger', value: '4'},
                        {text: 'pinky', value: '5'}
                    ]
                },
                fingerNumber: {
                    acceptReporters: true,
                    items: [
                        {text: 'base of hand', value: '0'},
                        {text: 'base of thumb', value: '1'},
                        {text: 'bottom of thumb', value: '2'},
                        {text: 'middle of thumb', value: '3'},
                        {text: 'top of thumb', value: '4'},
                        {text: 'base of index finger', value: '5'},
                        {text: 'bottom of index finger', value: '6'},
                        {text: 'middle of index finger', value: '7'},
                        {text: 'top of index finger', value: '8'},
                        {text: 'base of middle finger', value: '9'},
                        {text: 'bottom of middle finger', value: '10'},
                        {text: 'middle of middle finger', value: '11'},
                        {text: 'top of middle finger', value: '12'},
                        {text: 'base of ring finger', value: '13'},
                        {text: 'bottom of ring finger', value: '14'},
                        {text: 'middle of ring finger', value: '15'},
                        {text: 'top of ring finger', value: '16'},
                        {text: 'base of pinky', value: '17'},
                        {text: 'bottom of pinky', value: '18'},
                        {text: 'middle of pinky', value: '19'},
                        {text: 'top of pinky', value: '20'}
                    ]
                },
                personNumbers: {
                    acceptReporters: true,
                    items: ['1', '2', '3', '4', '5']
                }
            }
        };
    }

    // ═══════════════════════════════════════════════════════════
    //  Private helpers
    // ═══════════════════════════════════════════════════════════

    /** Load the MoveNet pose detection model (once). */
    async _loadPoseModel () {
        if (this._poseModelLoaded || this._poseModelLoading) return;
        this._poseModelLoading = true;
        try {
            await tf.ready();
            this._poseDetector = await poseDetection.createDetector(
                poseDetection.SupportedModels.MoveNet,
                {
                    modelType: poseDetection.movenet.modelType.MULTIPOSE_LIGHTNING,
                    enableSmoothing: true
                }
            );
            this._poseModelLoaded = true;
            console.log('[PoseNet] Pose model loaded successfully.');
        } catch (err) {
            console.error('[PoseNet] Pose model loading failed:', err);
        }
        this._poseModelLoading = false;
    }

    /** Create or return the hidden <video> element for webcam capture. */
    async _ensureVideo () {
        if (this._video && this._videoReady) return this._video;
        if (!this._video) {
            this._video = document.createElement('video');
            this._video.setAttribute('autoplay', '');
            this._video.setAttribute('playsinline', '');
            this._video.width = 480;
            this._video.height = 360;
            this._video.style.display = 'none';
            document.body.appendChild(this._video);
        }
        if (!this._videoReady) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {width: 480, height: 360}
                });
                this._video.srcObject = stream;
                await this._video.play();
                this._videoReady = true;
            } catch (err) {
                console.warn('[PoseNet] Camera access denied:', err);
            }
        }
        return this._video;
    }

    /** Get or create the transparent overlay canvas on top of the stage. */
    _ensureOverlay () {
        if (this._overlayCanvas) return this._overlayCanvas;
        const stageCanvas =
            document.querySelector('[class*="stage_stage_"] canvas') ||
            document.querySelector('canvas');
        if (!stageCanvas) return null;
        const parent = stageCanvas.parentElement;
        this._overlayCanvas = document.createElement('canvas');
        this._overlayCanvas.width = stageCanvas.width;
        this._overlayCanvas.height = stageCanvas.height;
        this._overlayCanvas.style.cssText =
            'position:absolute;top:0;left:0;width:100%;height:100%;' +
            'pointer-events:none;z-index:100;';
        parent.style.position = 'relative';
        parent.appendChild(this._overlayCanvas);
        this._overlayCtx = this._overlayCanvas.getContext('2d');
        return this._overlayCanvas;
    }

    /** Clear the overlay canvas. */
    _clearOverlay () {
        if (this._overlayCtx && this._overlayCanvas) {
            this._overlayCtx.clearRect(
                0, 0, this._overlayCanvas.width, this._overlayCanvas.height
            );
        }
    }

    /** Draw skeleton + keypoints on the overlay. */
    _renderOverlay () {
        if (!this._drawBBox) {
            this._clearOverlay();
            return;
        }
        this._ensureOverlay();
        if (!this._overlayCtx) return;
        this._clearOverlay();

        const ctx = this._overlayCtx;
        const cW = this._overlayCanvas.width;
        const cH = this._overlayCanvas.height;
        const srcW = (this._video && this._video.videoWidth) ? this._video.videoWidth : 480;
        const srcH = (this._video && this._video.videoHeight) ? this._video.videoHeight : 360;
        const scaleX = cW / srcW;
        const scaleY = cH / srcH;

        this._poses.forEach(pose => {
            const keypoints = pose.keypoints || [];
            const kpMap = {};
            keypoints.forEach(kp => {
                kpMap[kp.name] = kp;
            });

            // Draw skeleton lines
            ctx.strokeStyle = '#00ff00';
            ctx.lineWidth = 2;
            SKELETON_CONNECTIONS.forEach(([a, b]) => {
                const ptA = kpMap[a];
                const ptB = kpMap[b];
                if (ptA && ptB && (ptA.score || 0) > 0.3 && (ptB.score || 0) > 0.3) {
                    ctx.beginPath();
                    ctx.moveTo(ptA.x * scaleX, ptA.y * scaleY);
                    ctx.lineTo(ptB.x * scaleX, ptB.y * scaleY);
                    ctx.stroke();
                }
            });

            // Draw keypoints
            keypoints.forEach(kp => {
                if ((kp.score || 0) > 0.3) {
                    ctx.beginPath();
                    ctx.arc(kp.x * scaleX, kp.y * scaleY, 4, 0, 2 * Math.PI);
                    ctx.fillStyle = '#ff0000';
                    ctx.fill();
                    ctx.strokeStyle = '#ffffff';
                    ctx.lineWidth = 1;
                    ctx.stroke();
                }
            });
        });

        // Draw hand keypoints
        if (this._hands) {
            this._hands.forEach(hand => {
                const keypoints = hand.keypoints || [];
                keypoints.forEach(kp => {
                    if (kp && kp.x !== undefined && kp.y !== undefined) {
                        ctx.beginPath();
                        ctx.arc(kp.x * scaleX, kp.y * scaleY, 3, 0, 2 * Math.PI);
                        ctx.fillStyle = '#00ccff';
                        ctx.fill();
                    }
                });

                // Draw hand connections
                const fingerGroups = [
                    [0, 1, 2, 3, 4], // thumb
                    [0, 5, 6, 7, 8], // index
                    [0, 9, 10, 11, 12], // middle
                    [0, 13, 14, 15, 16], // ring
                    [0, 17, 18, 19, 20] // pinky
                ];
                ctx.strokeStyle = '#00ccff';
                ctx.lineWidth = 1.5;
                fingerGroups.forEach(group => {
                    for (let i = 0; i < group.length - 1; i++) {
                        const a = keypoints[group[i]];
                        const b = keypoints[group[i + 1]];
                        if (a && b) {
                            ctx.beginPath();
                            ctx.moveTo(a.x * scaleX, a.y * scaleY);
                            ctx.lineTo(b.x * scaleX, b.y * scaleY);
                            ctx.stroke();
                        }
                    }
                });
            });
        }
    }

    /** Find a keypoint by its ID in a specific pose. */
    _getKeypoint (poseIdx, bodyPartId) {
        const pose = this._poses[poseIdx];
        if (!pose || !pose.keypoints) return null;
        const partName = BODY_PART_MAP[bodyPartId];
        if (!partName) return null;
        const kp = pose.keypoints.find(k => k.name === partName);
        return (kp && (kp.score || 0) > 0.3) ? kp : null;
    }

    // ═══════════════════════════════════════════════════════════
    //  Block implementations
    // ═══════════════════════════════════════════════════════════

    async toggleStageVideoFeed (args) {
        const state = args.VIDEO_STATE;
        const transparency = Math.max(0, Math.min(100, Number(args.TRANSPARENCY) || 0));
        try {
            const video = this.runtime.ioDevices.video;
            if (state === 'on') {
                video.enableVideo();
                await this._ensureVideo();
            } else {
                video.disableVideo();
            }
            video.setPreviewGhost(transparency);
        } catch (err) {
            if (state === 'on') await this._ensureVideo();
        }
    }

    drawBoundingBox (args) {
        this._drawBBox = args.OPTION === '1';
        if (!this._drawBBox) this._clearOverlay();
        else this._renderOverlay();
    }

    async analyseImage (args) {
        await this._loadPoseModel();
        const video = await this._ensureVideo();
        if (!video || !this._videoReady || !this._poseDetector) {
            this._poses = [];
            return;
        }
        try {
            const poses = await this._poseDetector.estimatePoses(video, {
                maxPoses: 5,
                flipHorizontal: true
            });
            this._poses = poses || [];
        } catch (err) {
            console.warn('[PoseNet] Pose analysis error:', err);
            this._poses = [];
        }
        this._renderOverlay();
    }

    async analyseImageHand (args) {
        // Use pose detection to find wrist positions as hand proxies
        // This ensures hand detection works even without a separate hand model
        await this._loadPoseModel();
        const video = await this._ensureVideo();
        if (!video || !this._videoReady || !this._poseDetector) {
            this._hands = [];
            return;
        }
        try {
            const poses = await this._poseDetector.estimatePoses(video, {
                maxPoses: 5,
                flipHorizontal: true
            });
            this._poses = poses || [];

            // Build hand data from wrist keypoints
            this._hands = [];
            this._poses.forEach(pose => {
                if (!pose.keypoints) return;
                // Extract wrist-region keypoints for each detected person
                const leftWrist = pose.keypoints.find(k => k.name === 'left_wrist');
                const rightWrist = pose.keypoints.find(k => k.name === 'right_wrist');
                const leftElbow = pose.keypoints.find(k => k.name === 'left_elbow');
                const rightElbow = pose.keypoints.find(k => k.name === 'right_elbow');

                if (leftWrist && (leftWrist.score || 0) > 0.3) {
                    this._hands.push({
                        keypoints: this._estimateHandKeypoints(leftWrist, leftElbow),
                        handedness: 'Left'
                    });
                }
                if (rightWrist && (rightWrist.score || 0) > 0.3) {
                    this._hands.push({
                        keypoints: this._estimateHandKeypoints(rightWrist, rightElbow),
                        handedness: 'Right'
                    });
                }
            });
        } catch (err) {
            console.warn('[PoseNet] Hand analysis error:', err);
            this._hands = [];
        }
        this._renderOverlay();
    }

    /**
     * Estimate 21 hand keypoints from wrist + elbow positions.
     * This creates approximate finger positions based on the wrist-to-elbow vector.
     */
    _estimateHandKeypoints (wrist, elbow) {
        const wx = wrist.x;
        const wy = wrist.y;

        // Direction from elbow to wrist (hand extends from wrist)
        let dx = 0;
        let dy = -15; // default: hand points upward
        if (elbow && (elbow.score || 0) > 0.3) {
            dx = (wx - elbow.x) * 0.3;
            dy = (wy - elbow.y) * 0.3;
        }

        const keypoints = [];
        // Wrist (index 0)
        keypoints.push({x: wx, y: wy, name: 'wrist'});

        // Generate approximate finger positions radiating from wrist
        const fingers = [
            {name: 'thumb', angle: -0.6, len: [0.3, 0.5, 0.7, 0.9]},
            {name: 'index_finger', angle: -0.25, len: [0.4, 0.6, 0.8, 1.0]},
            {name: 'middle_finger', angle: 0, len: [0.45, 0.65, 0.85, 1.1]},
            {name: 'ring_finger', angle: 0.25, len: [0.4, 0.6, 0.8, 1.0]},
            {name: 'pinky', angle: 0.5, len: [0.35, 0.5, 0.65, 0.8]}
        ];

        const suffixes = ['_mcp', '_pip', '_dip', '_tip'];
        // For thumb: _cmc, _mcp, _ip, _tip
        const thumbSuffixes = ['_cmc', '_mcp', '_ip', '_tip'];

        fingers.forEach(finger => {
            const cos = Math.cos(finger.angle);
            const sin = Math.sin(finger.angle);
            const suf = finger.name === 'thumb' ? thumbSuffixes : suffixes;

            finger.len.forEach((l, i) => {
                const fx = wx + (dx * cos - dy * sin) * l * 2;
                const fy = wy + (dx * sin + dy * cos) * l * 2;
                keypoints.push({
                    x: fx,
                    y: fy,
                    name: `${finger.name}${suf[i]}`
                });
            });
        });

        return keypoints;
    }

    // ── Pose reporters ──────────────────────────────────────────

    getPeopleCount () {
        return this._poses.length;
    }

    getX (args) {
        const personIdx = (parseInt(args.PERSON, 10) || 1) - 1;
        const kp = this._getKeypoint(personIdx, args.PART);
        if (!kp) return 0;
        const vw = (this._video && this._video.videoWidth) ? this._video.videoWidth : 480;
        return Math.round((kp.x / vw * 480) - 240);
    }

    getY (args) {
        const personIdx = (parseInt(args.PERSON, 10) || 1) - 1;
        const kp = this._getKeypoint(personIdx, args.PART);
        if (!kp) return 0;
        const vh = (this._video && this._video.videoHeight) ? this._video.videoHeight : 360;
        return Math.round(180 - (kp.y / vh * 360));
    }

    // ── Hand reporters ──────────────────────────────────────────

    getPositionHand (args) {
        const hand = this._hands[0];
        if (!hand || !hand.keypoints) return 0;

        // FINGER: 1=thumb, 2=index, 3=middle, 4=ring, 5=pinky
        // PART (partsHand): 4=top, 3=middle, 2=bottom, 1=base
        
        let fingerIdxOffset = (parseInt(args.FINGER, 10) - 1) * 4;
        if (fingerIdxOffset < 0) fingerIdxOffset = 0;
        
        // Map part 4,3,2,1 to offset within the finger
        let partOffset = 4 - parseInt(args.PART, 10);
        if (partOffset < 0 || partOffset > 3) partOffset = 0;
        
        // thumb has 1 to 4, others 5 to 8, 9 to 12, etc.
        const kpIdx = fingerIdxOffset + partOffset + 1;
        
        const kp = hand.keypoints[kpIdx];
        if (!kp) return 0;

        if (args.POSITION === '1') {
            const vw = (this._video && this._video.videoWidth) ? this._video.videoWidth : 480;
            return Math.round((kp.x / vw * 480) - 240);
        }
        const vh = (this._video && this._video.videoHeight) ? this._video.videoHeight : 360;
        return Math.round(180 - (kp.y / vh * 360));
    }

    getPositionNumberHand (args) {
        const hand = this._hands[0];
        if (!hand || !hand.keypoints) return 0;

        const partIdx = parseInt(args.PART, 10) || 4;
        const kp = hand.keypoints[partIdx];
        if (!kp) return 0;

        if (args.POSITION === '1') {
            const vw = (this._video && this._video.videoWidth) ? this._video.videoWidth : 480;
            return Math.round((kp.x / vw * 480) - 240);
        }
        const vh = (this._video && this._video.videoHeight) ? this._video.videoHeight : 360;
        return Math.round(180 - (kp.y / vh * 360));
    }

    getHand (args) {
        const hand = this._hands[0];
        if (!hand || !hand.keypoints) return 0;
        const kp = hand.keypoints[0]; // wrist is the base position
        if (!kp) return 0;
        if (args.POSITION === '1') {
            const vw = (this._video && this._video.videoWidth) ? this._video.videoWidth : 480;
            return Math.round((kp.x / vw * 480) - 240);
        }
        const vh = (this._video && this._video.videoHeight) ? this._video.videoHeight : 360;
        return Math.round(180 - (kp.y / vh * 360));
    }

    // ── Booleans ────────────────────────────────────────────────

    isDetected (args) {
        const personIdx = (parseInt(args.PERSON, 10) || 1) - 1;
        const kp = this._getKeypoint(personIdx, args.PART);
        return kp !== null;
    }

    isDetectedHand () {
        return this._hands.length > 0;
    }
}

export default PosenetExtension;
